"use client";

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TempLogo } from "@/assets/temp-logo";
import { useResetPassword } from "../../api/auth.queries";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import VerifyEmail from "../../pages/VerifyEmailContainer";
import Footer from "@/components/Common/Footer";
import { Spinner } from "@/components/ui/spinner";

const commonPasswords = [
  "12345678",
  "password",
  "password123",
  "qwerty",
  "abc123",
  "abcd1234",
  "letmein",
  "iloveyou",
  "admin123",
];
const changePasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters long")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/\d/, "Password must contain at least one number")
      .regex(
        /[!@#$%^&*(),.?":{}|<>_]/,
        "Password must contain at least one special character",
      )
      .refine(
        (val) => !commonPasswords.includes(val.toLowerCase()),
        "Password is too common, please choose another one",
      ),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ChangePasswordSchema = z.infer<typeof changePasswordSchema>;

export default function ChangePassword({
  token,
  userId,
}: {
  token: string;
  userId: string;
}) {
  const navigate = useNavigate();
  const { mutate: resetPassword, isPending } = useResetPassword();
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<ChangePasswordSchema>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  function onSubmit(values: ChangePasswordSchema) {
    resetPassword(
      {
        token,
        user_id: userId,
        new_password: values.newPassword,
        app_id: "68958c254d1efc253f7380e0",
      },
      {
        onSuccess: () => {
          setShowVerifyEmail(true);
        },
      },
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-between px-4 pb-8">
      {showVerifyEmail ? (
        <VerifyEmail />
      ) : (
        <div className="flex w-full max-w-[420px] flex-grow flex-col justify-center gap-8">
          <div className="text-center">
            <div className="flex justify-center">
              <TempLogo width={68} height={44} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">metawhale</h1>
          </div>

          <div>
            <p
              className="text-muted mb-8 flex cursor-pointer flex-row items-center gap-1 text-sm leading-normal font-bold tracking-normal"
              onClick={() => navigate({ to: "/login" })}
            >
              <i className="ri-arrow-left-long-line"></i> Back
            </p>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                {/* New Password */}
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-sm font-bold text-neutral-800">
                        New Password{" "}
                        <span className="text-sm text-red-600">*</span>
                      </FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showNewPassword ? "text" : "password"}
                            placeholder="Enter your new password"
                            className="h-[45px]"
                            variant="filled"
                            {...field}
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? (
                            <i className="ri-eye-off-line"></i>
                          ) : (
                            <i className="ri-eye-line"></i>
                          )}
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Confirm Password */}
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className="text-sm font-bold text-neutral-800">
                        Confirm Password{" "}
                        <span className="text-sm text-red-600">*</span>
                      </FormLabel>
                      <div className="relative">
                        <FormControl>
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm your new password"
                            className="h-[45px]"
                            variant="filled"
                            {...field}
                          />
                        </FormControl>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-1/2 right-2 h-8 w-8 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                        >
                          {showConfirmPassword ? (
                            <i className="ri-eye-off-line"></i>
                          ) : (
                            <i className="ri-eye-line"></i>
                          )}
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Submit */}
                <Button
                  type="submit"
                  className="text-md mt-6 h-[48px] w-full leading-normal"
                  disabled={isPending}
                >
                  {isPending ? (
                    <div className="flex h-full items-center justify-center text-white">
                      <Spinner size="sm" className="stroke-white!" />
                    </div>
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </form>
            </Form>
          </div>

          <div className="text-center text-sm font-semibold text-neutral-800">
            If you have any questions, please contact <br />
            <a
              href="mailto:support@media-meter.com"
              className="text-brand-600 hover:text-orange-600"
            >
              support@media-meter.com
            </a>
            . We are happy to help!
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
}
